// cSpell:ignore xyflow Silae SILAE
import { type NodeProps, type Node } from "@xyflow/react";
import { Ban } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";

import { useSilaeFormatters } from "@/hooks/useSilaeFormatters";
import type { GraphNodeData } from "../utils/buildGraph";
import { effectiveConditionOperators } from "../utils/effectiveConditionOperators";
import { logicalOperatorLabel } from "../utils/logicalOperatorLabel";
import { RichFormattedText } from "../utils/RichFormattedText";
import { NodeShell } from "./NodeShell";

/** Détermine la clé i18n de la description selon les opérateurs utilisés. */
function descriptionKey(ops: string[], condCount: number): string {
  if (condCount <= 1) return "step5Validation.exclusions.descriptionSingle";
  const effective = effectiveConditionOperators(ops, condCount);
  const unique = new Set(effective.map((o) => o.toUpperCase()));
  if (unique.size > 1) return "step5Validation.exclusions.descriptionMixed";
  const op = [...unique][0];
  return op === "OR"
    ? "step5Validation.exclusions.descriptionAllOr"
    : "step5Validation.exclusions.descriptionAllAnd";
}

export function ExclusionsNode({ data }: NodeProps<Node<GraphNodeData>>) {
  const { t } = useTranslation();
  const { formatConditionDescription } = useSilaeFormatters();
  const conditions = data.conditions ?? [];
  const ops = data.conditionsOperators ?? [];
  const i18nKey = descriptionKey(ops, conditions.length);

  return (
    <NodeShell
      variant="exclusions"
      icon={Ban}
      title={t("step5Validation.exclusions.title")}
      targetStep={data.targetStep}
    >
      <p className="mb-2 text-[11px] leading-snug text-zinc-600 dark:text-zinc-400">
        <Trans
          i18nKey={i18nKey}
          components={{
            b: (
              <span className="font-semibold text-zinc-800 dark:text-zinc-200" />
            ),
          }}
        />
      </p>
      <ul className="space-y-1.5">
        {conditions.map((cond, i) => (
          <li key={cond.id} className="flex flex-col items-stretch gap-1">
            <div className="flex gap-2 rounded-md border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900 px-2 py-1.5 wrap-break-word">
              <span className="font-mono text-[10px] text-zinc-500 dark:text-zinc-400 mt-0.5 shrink-0">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="text-zinc-900 dark:text-zinc-50">
                <RichFormattedText text={formatConditionDescription(cond)} />
              </span>
            </div>
            {i < conditions.length - 1 && (
              <span className="self-center text-[10px] font-bold tracking-wider text-rose-700 dark:text-rose-400">
                {logicalOperatorLabel(ops[i], t)}
              </span>
            )}
          </li>
        ))}
      </ul>
    </NodeShell>
  );
}
